<section class="rnd-page">
  <div class="rnd-header">
    <div>
      <p class="eyebrow">R&D Lab</p>
      <h1>Voice Bot to Speech</h1>
      <p class="subtext">Record a voice sample, transcribe it, send the text to chatbot getAnswer, then play the generated Urdu voice response.</p>
    </div>
    <div class="status-pill" [class.recording]="state === 'recording'" [class.failed]="state === 'failed'">
      <span></span>
      {{ statusLabel }}
    </div>
  </div>

  <div class="rnd-grid">
    <div class="panel recorder-panel">
      <div class="timer">{{ formattedDuration }}</div>
      <div class="meter" [class.live]="state === 'recording'">
        <span></span><span></span><span></span><span></span><span></span>
      </div>

      <div class="actions">
        <button type="button" class="primary" [disabled]="!canRecord" (click)="startRecording()">
          <span class="material-icons">fiber_manual_record</span>
          Start Recording
        </button>
        <button type="button" class="danger" [disabled]="!canStop" (click)="stopRecording()">
          <span class="material-icons">stop</span>
          Stop
        </button>
        <button type="button" class="ghost" [disabled]="state === 'recording' || state === 'uploading' || state === 'synthesizing'" (click)="clear()">
          <span class="material-icons">refresh</span>
          Clear
        </button>
      </div>

      <div class="audio-box" *ngIf="audioUrl; else noAudio">
        <div class="audio-meta">
          <span>Size: {{ fileSizeLabel }}</span>
          <span>Type: {{ mimeType || 'audio/webm' }}</span>
        </div>
        <audio [src]="audioUrl" controls></audio>
      </div>

      <ng-template #noAudio>
        <div class="empty-audio">
          <span class="material-icons">mic</span>
          No recording yet
        </div>
      </ng-template>

      <button type="button" class="submit" [disabled]="!canSend" (click)="sendToBotVoice()">
        <span class="material-icons">record_voice_over</span>
        Generate Voice Answer
      </button>
    </div>

    <div class="panel result-panel">
      <div class="panel-title">
        <span class="material-icons">forum</span>
        Bot Response
      </div>

      <div class="error" *ngIf="errorMessage">
        {{ errorMessage }}
      </div>

      <div class="result-stack">
        <div class="result-block">
          <label>Transcription</label>
          <div class="transcript" *ngIf="transcription; else emptyTranscript">{{ transcription }}</div>
          <ng-template #emptyTranscript>
            <div class="empty-result">Transcription will appear here after upload.</div>
          </ng-template>
        </div>

        <div class="result-block">
          <label>Chatbot Answer</label>
          <div class="answer" *ngIf="answerText; else emptyAnswer">{{ answerText }}</div>
          <ng-template #emptyAnswer>
            <div class="empty-result">Chatbot answer will appear here.</div>
          </ng-template>
        </div>

        <div class="result-block">
          <label>Generated Audio</label>
          <div class="audio-box" *ngIf="ttsAudioUrl; else emptyTts">
            <audio [src]="ttsAudioUrl" controls autoplay></audio>
          </div>
          <ng-template #emptyTts>
            <div class="empty-result">Generated voice will appear here.</div>
          </ng-template>
        </div>
      </div>

      <div class="raw-buttons">
        <button type="button" class="raw-toggle" *ngIf="rawTranscriptionResponse" (click)="showRawTranscription = !showRawTranscription">
          {{ showRawTranscription ? 'Hide' : 'Show' }} transcription JSON
        </button>
        <button type="button" class="raw-toggle" *ngIf="rawGetAnswerResponse" (click)="showRawAnswer = !showRawAnswer">
          {{ showRawAnswer ? 'Hide' : 'Show' }} getAnswer JSON
        </button>
        <button type="button" class="raw-toggle" *ngIf="rawTtsResponse" (click)="showRawTts = !showRawTts">
          {{ showRawTts ? 'Hide' : 'Show' }} TTS JSON
        </button>
      </div>
      <pre class="raw" *ngIf="showRawTranscription">{{ rawTranscriptionResponse }}</pre>
      <pre class="raw" *ngIf="showRawAnswer">{{ rawGetAnswerResponse }}</pre>
      <pre class="raw" *ngIf="showRawTts">{{ rawTtsResponse }}</pre>
    </div>
  </div>

  <div class="panel saved-panel">
    <div class="panel-title">
      <span class="material-icons">dataset</span>
      Saved Voice TTS Experiments
      <button type="button" class="refresh-mini" [disabled]="recordsLoading" (click)="loadSavedRecords()">
        <span class="material-icons">refresh</span>
        Refresh
      </button>
    </div>

    <div class="empty-result" *ngIf="!recordsLoading && !savedRecords.length">
      No saved voice TTS experiments yet.
    </div>

    <div class="table-wrap" *ngIf="savedRecords.length">
      <table>
        <thead>
          <tr>
            <th>Recorded</th>
            <th>Source</th>
            <th>Transcription</th>
            <th>Answer</th>
            <th>Voice</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let record of savedRecords">
            <td>
              <strong>{{ dateFor(record.recordedAt || record.createdAt) }}</strong>
              <span>{{ durationFor(record.durationSeconds) }} · {{ fileSizeFor(record.fileSizeBytes) }}</span>
            </td>
            <td class="audio-cell">
              <button type="button" class="icon-button" (click)="previewSavedAudio(record, 'input')">
                <span class="material-icons">{{ record.inputPreviewUrl ? 'close' : 'play_arrow' }}</span>
                {{ record.inputPreviewUrl ? 'Hide' : 'Play' }}
              </button>
              <audio *ngIf="record.inputPreviewUrl" [src]="record.inputPreviewUrl" controls></audio>
            </td>
            <td class="text-cell">{{ record.transcriptionText || '-' }}</td>
            <td class="text-cell">
              {{ record.answerText || '-' }}
              <span>Similarity: {{ confidenceFor(record.answerSimilarityScore) }}</span>
            </td>
            <td class="audio-cell">
              <button type="button" class="icon-button" [disabled]="record.status !== 'COMPLETED'" (click)="previewSavedAudio(record, 'tts')">
                <span class="material-icons">{{ record.ttsPreviewUrl ? 'close' : 'volume_up' }}</span>
                {{ record.ttsPreviewUrl ? 'Hide' : 'Play' }}
              </button>
              <audio *ngIf="record.ttsPreviewUrl" [src]="record.ttsPreviewUrl" controls></audio>
              <span>{{ record.ttsVoice || 'ur-PK-UzmaNeural' }}</span>
            </td>
            <td>
              <strong [class.failed-status]="record.status === 'FAILED'">{{ record.status || '-' }}</strong>
              <span>{{ record.errorMessage || record.ttsModel || '-' }}</span>
            </td>
            <td>
              <button type="button" class="delete-mini" (click)="deleteSavedRecord(record)">
                <span class="material-icons">delete</span>
                Delete
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</section>
